import { useState, useEffect, useCallback } from 'react';
import { API } from '../config/apiBase';


// Maps frontend menu keys to plan modules
const MENU_MODULE_MAP = {
    'ponto': 'ponto_digital',
    'emp-performance': 'performance',
    'emp-financeiro': 'financeiro_completo',
    'emp-escalas': 'escalas_jornadas',
    'emp-permissoes': 'permissoes_avancadas',
    'col-historico': 'meu_historico',
    'emp-solicitacoes': 'solicitacoes',
    'col-solicitacoes': 'solicitacoes',
    'descobertas': 'radar_ofertas',
};

// Maps blocked modules to the minimum plan that unlocks them
const UPGRADE_MAP = {
    'ponto_digital_inicial': { plan: 'lite', price: 9.90 },
    'ponto_digital': { plan: 'start', price: 19.90 },
    'ponto_digital_premium': { plan: 'pro', price: 149.90 },
    'performance': { plan: 'gestao', price: 79.90 },
    'financeiro_basico': { plan: 'lite', price: 9.90 },
    'financeiro_melhorado': { plan: 'start', price: 19.90 },
    'financeiro_completo': { plan: 'gestao', price: 79.90 },
    'escalas_jornadas': { plan: 'gestao', price: 79.90 },
    'escalas_jornadas_basicas': { plan: 'gestao', price: 79.90 },
    'permissoes_avancadas': { plan: 'pro', price: 149.90 },
    'auditoria_completa': { plan: 'pro', price: 149.90 },
    'auditoria_resumida': { plan: 'gestao', price: 79.90 },
    'exportacao_pdf': { plan: 'gestao', price: 79.90 },
    'exportacao_csv_completa': { plan: 'start', price: 19.90 },
    'logo_personalizada': { plan: 'lite', price: 9.90 },
    'branding': { plan: 'lite', price: 9.90 },
    'performance_avancada': { plan: 'pro', price: 149.90 },
    'relatorios_avancados': { plan: 'pro', price: 149.90 },
    'geofence_avancado': { plan: 'pro', price: 149.90 },
    'solicitacoes': { plan: 'lite', price: 9.90 },
    'meu_historico': { plan: 'start', price: 19.90 },
    'radar_ofertas': { plan: 'gestao', price: 79.90 },
    'radar_ofertas_premium': { plan: 'pro', price: 149.90 },
};

const PLAN_NAMES = {
    free: 'Free',
    lite: 'Lite',
    start: 'Start',
    gestao: 'Gestao',
    pro: 'Pro',
    enterprise: 'Enterprise',
};

export function usePlanLimits() {
    const [planInfo, setPlanInfo] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchPlanInfo = useCallback(async () => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) { setLoading(false); return; }
        try {
            const res = await fetch(`${API}/empresa/plano-info`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPlanInfo(data);
            }
        } catch (e) {
            console.error('Failed to fetch plan info', e);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchPlanInfo(); }, [fetchPlanInfo]);

    const hasModule = useCallback((moduleKey) => {
        if (!planInfo) return true; // Default allow until loaded
        const modules = planInfo.modules || [];
        return modules.includes(moduleKey);
    }, [planInfo]);

    const isMenuBlocked = useCallback((menuKey) => {
        const moduleKey = MENU_MODULE_MAP[menuKey];
        if (!moduleKey) return false;
        return !hasModule(moduleKey);
    }, [hasModule]);

    const getUpgradeInfo = useCallback((moduleKey) => {
        const info = UPGRADE_MAP[moduleKey] || { plan: 'start', price: 19.90 };
        return {
            ...info,
            planName: PLAN_NAMES[info.plan] || info.plan,
        };
    }, []);

    const checkLimit = useCallback((limitKey) => {
        if (!planInfo || !planInfo.limits) return { allowed: true };
        const limit = planInfo.limits[limitKey];
        if (!limit) return { allowed: true };
        return {
            allowed: limit.current < limit.max,
            current: limit.current,
            max: limit.max,
        };
    }, [planInfo]);

    return {
        planInfo,
        loading,
        hasModule,
        isMenuBlocked,
        getUpgradeInfo,
        checkLimit,
        refresh: fetchPlanInfo,
        plan: planInfo?.plan || 'free',
        planName: PLAN_NAMES[planInfo?.plan] || 'Free',
        MENU_MODULE_MAP,
        UPGRADE_MAP,
        PLAN_NAMES,
    };
}
